import Spinner from '@xo-union/tk-component-spinner';
import gql from 'graphql-tag';
import { get, set } from 'js-cookie';
import moment from 'moment';
import React, { useContext, useEffect } from 'react';

import { useWeddingWebsiteQuery, WeddingWebsiteQuery } from '../../generated/graphql';
import { TrackingContext } from '../../TrackingContext';
import FirstInteraction from '../FirstInteraction';
import styles from './dashboard.scss';
import DashboardContent from './DashboardContent';
import { ManageRsvpSettingsRail } from '../ManageRsvpSettingsRail';
import { useHistory } from 'react-router-dom';
import CvMigrationModal from '../CVMigrationModal';
import { onboardingDesignPLPRoute } from '../../helpers/routes';
import { withCurrentQueryParams } from '../../helpers/withCurrentQueryParams';
import { isMissingWeddingWebsiteError } from '../../helpers/isMissingWeddingWebsiteError';

export const CV2_FIRST_IMPRESSION_KEY = 'firstCv2Interaction';

const getDashboardImpressionReason = (wedding?: WeddingWebsiteQuery['weddingWebsite']) => {
  const firstCv2Interaction = get(CV2_FIRST_IMPRESSION_KEY);
  if (!wedding || !!firstCv2Interaction) {
    return null;
  }
  set(CV2_FIRST_IMPRESSION_KEY, moment().toISOString());
  if (!wedding?.firstDashboardInteraction) {
    return 'first load';
  } else {
    return 'first load admin 2.0';
  }
};

export const weddingWebsiteQuery = gql`
  query weddingWebsiteQuery {
    weddingWebsite {
      id
      announcement {
        id
        message
        show
      }
      legacyMigrated
      searchable
      pin
      pinProtect
      state
      websiteCreatedAt
      source
      purchaseDomain {
        id
        name
        showRenewal
        isRenewable
        isInRedemptionPeriod
        isExpired
        expirationDate
        dateOfLastRenewal
        renewalPrice
        createdAt
      }
      vanityRoot
      vanityUrl
      slug
      registryNote
      coverPhoto {
        mediaApiId
        mediaUrl
        cropX
        cropY
        width
        height
        originalMediaUrl
      }
      state
      hideCountdown
      hideDate
      weddingDateFormat
      weddingDateRangeStart
      weddingDateRangeEnd
      weddingLocation
      themeId
      firstDashboardInteraction
      adminVersion
      isNewTemplate
      wwsUrl
      layout
    }
    member {
      id
      firstName
      lastName
      fianceFirstName
      fianceLastName
      email
      isEmailVerified
      aiSettings {
        useConversationalMemory
      }
    }
    wedding {
      id
      weddingDate
      guestRange
      season {
        name
        year
      }
    }
    guestWedding {
      id
      createdAt
      updatedAt
      isPrivateRsvp
      sendRsvpReminder
      allowGuestPreview
      usesSubEvents
      questions {
        id
        text
        type
        isRequired
        answerType
        answerDestination
        options {
          id
          text
          description
        }
      }
      rsvpDeadline
      rsvpMedium
      rsvpPageHidden
      rsvpWorkflow
    }
  }
`;

const Dashboard: React.FC = () => {
  const { loading, data, error } = useWeddingWebsiteQuery();
  const { track, dashboardImpressionFired, setDashboardImpressionFired } = useContext(TrackingContext);
  const history = useHistory();
  const backToDashboard = () => history.push('/gs/dashboard');

  if (
    !loading &&
    (isMissingWeddingWebsiteError(error) || data?.weddingWebsite?.state === 'uninitialized') &&
    process.env.REACT_APP_REDIRECT_UNINITIALIZED_WWS === 'true'
  ) {
    // keep inbound marketing params (utm_*, gclid, ...) across the redirect
    window.location.href = withCurrentQueryParams(onboardingDesignPLPRoute, window.location.search);
  }

  useEffect(() => {
    if (!!data?.weddingWebsite && !dashboardImpressionFired) {
      track('WWS Dashboard Impression', {
        reason: getDashboardImpressionReason(data?.weddingWebsite),
        wwsSource: data?.weddingWebsite?.source,
      });
      setDashboardImpressionFired(true);
    }
  }, [data, track, dashboardImpressionFired, setDashboardImpressionFired]);

  useEffect(() => {
    setDashboardImpressionFired(true);
  }, [setDashboardImpressionFired]);

  return (
    <>
      <ManageRsvpSettingsRail onClose={backToDashboard} onSaveSuccess={backToDashboard} />
      <CvMigrationModal />

      <FirstInteraction weddingWebsite={data?.weddingWebsite} member={data?.member} />
      <div className={styles.container}>
        <div className={styles.wrapper}>
          {loading ? (
            <div className={styles.loadingContainer}>
              <Spinner />
            </div>
          ) : (
            <DashboardContent />
          )}
        </div>
      </div>
    </>
  );
};

export default Dashboard;
